// v4 (V4-P2, spec-v4-structure-and-stage.md 5.2): the one-line summaries on fold rows. Pure, so they're tested
// without a DOM: a fold's row says what's inside in a few words ("v2 in use · 3 h ago", "300 a day").
import type { SpendingView } from "./spending";

/** The Versions fold's one line: which version is in use and when it was made ("v2 in use · 3 h ago"). */
export function versionsSummary(versions: readonly { version: number; isLatest: boolean; createdAt: string }[], now = Date.now()): string {
  if (!versions.length) return "None yet";
  const inUse = versions.find((v) => v.isLatest) ?? [...versions].sort((a, b) => b.version - a.version)[0]!;
  const ago = agoWord(inUse.createdAt, now);
  return [`v${inUse.version} in use`, ago].filter(Boolean).join(" · ");
}

/** "just now", "5 min ago", "3 h ago", "2 days ago" (empty for a bad date). */
export function agoWord(iso: string, now = Date.now()): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return "";
  const min = Math.max(0, Math.round((now - t) / 60000));
  if (min < 1) return "just now";
  if (min < 60) return `${min} min ago`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h} h ago`;
  const d = Math.round(h / 24);
  return d === 1 ? "yesterday" : `${d} days ago`;
}

/** The first non-empty line of some text, for a fold's one-line summary. */
export function firstLine(text: string): string {
  return (text.split(/\r?\n/).find((l) => l.trim()) ?? "").trim();
}

/** Settings > Spending's row: the limits that are set, or that none are. */
export function limitsSummary(v: Pick<SpendingView, "limits">): string {
  const parts = [v.limits.daily !== null && `${v.limits.daily} a day`, v.limits.project !== null && `${v.limits.project} a video`].filter(Boolean) as string[];
  return parts.length ? parts.join(" · ") : "No limits set";
}

/**
 * v4 rolling hold: a Director call writes a ledger entry when it starts, one a minute while it runs and one when it
 * ends (all with ref `director:<session>`). History shows each call as one line (newest first, where its latest entry
 * was) with what it cost in all; a call that cost nothing drops out. Other entries pass through unchanged.
 */
export function groupDirectorCalls<T extends { delta: number; reason: string; ref?: string | null }>(ledger: readonly T[]): T[] {
  const out: T[] = [];
  const at = new Map<string, number>();
  for (const l of ledger) {
    const ref = l.ref ?? "";
    if (!ref.startsWith("director:")) {
      out.push(l);
      continue;
    }
    const i = at.get(ref);
    if (i === undefined) {
      at.set(ref, out.length);
      out.push({ ...l, reason: "director" });
    } else out[i] = { ...out[i]!, delta: out[i]!.delta + l.delta };
  }
  return out.filter((l) => !(l.ref ?? "").startsWith("director:") || l.delta !== 0);
}

/** Credits > History's row: the latest entry ("−15 · 2 h ago"), or nothing yet. */
export function ledgerSummary(ledger: readonly { delta: number; createdAt?: string }[], now = Date.now()): string {
  const last = ledger[0];
  if (!last) return "Nothing yet";
  const n = Math.abs(last.delta).toLocaleString("en-US");
  return [`${last.delta > 0 ? "+" : "−"}${n}`, last.createdAt ? agoWord(last.createdAt, now) : ""].filter(Boolean).join(" · ");
}
